import { useEffect, useState, useRef } from 'react';

export default function ProblemSolution() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const problems = [
    'Fragmented listings across multiple platforms',
    'Unverified agents and property information',
    'Manual paperwork and slow processes',
    'Price opacity and market uncertainty'
  ];

  const solutions = [
    'One unified platform for all properties',
    'Verified properties with complete history',
    'Transparent pricing and market data',
    'Secure digital transactions'
  ];

  return (
    <section ref={sectionRef} className="py-32 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Problems */}
          <div className={`transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div 
              className="p-10 rounded-3xl"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(14px)',
                border: '1px solid rgba(30, 79, 255, 0.1)',
                boxShadow: '0 4px 24px rgba(30, 79, 255, 0.06)'
              }}
            >
              <h2 className="text-4xl font-semibold mb-8" style={{ color: '#0B1C2D' }}>
                The Challenge
              </h2>
              
              <div className="space-y-6">
                {problems.map((problem, index) => (
                  <div 
                    key={index}
                    className={`flex items-start gap-4 transition-all duration-700`}
                    style={{ 
                      transitionDelay: `${(index + 1) * 150}ms`,
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0)' : 'translateY(10px)'
                    }}
                  >
                    <div 
                      className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                      style={{ backgroundColor: '#1E4FFF' }}
                    ></div>
                    <p className="text-lg leading-relaxed" style={{ color: '#6B7A99' }}>
                      {problem}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Solutions */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="p-10 rounded-3xl bg-white" style={{ boxShadow: '0 4px 24px rgba(30, 79, 255, 0.08)' }}>
              <h2 className="text-4xl font-semibold mb-8" style={{ color: '#0B1C2D' }}>
                The Solution
              </h2>
              
              <div className="space-y-6">
                {solutions.map((solution, index) => (
                  <div 
                    key={index}
                    className={`flex items-start gap-4 transition-all duration-700`}
                    style={{ 
                      transitionDelay: `${(index + 1) * 150 + 300}ms`,
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0)' : 'translateY(10px)'
                    }}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <i className="ri-checkbox-circle-fill text-2xl" style={{ color: '#1E4FFF' }}></i>
                    </div>
                    <p className="text-lg leading-relaxed" style={{ color: '#0B1C2D' }}>
                      {solution.split(' ').map((word, i) => {
                        const highlightWords = ['unified', 'Verified', 'Transparent', 'Secure'];
                        return highlightWords.includes(word) ? (
                          <strong key={i} style={{ color: '#1E4FFF' }}>{word} </strong>
                        ) : (
                          <span key={i}>{word} </span>
                        );
                      })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
